import React from 'react';
import { Box, Typography, Container, Grid, Avatar, AvatarGroup, Stack, useTheme, alpha, Button, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import BuildIcon from '@mui/icons-material/Build';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GitHubIcon from '@mui/icons-material/GitHub';

const roadmap = [
  { stage: 'Phase 1', title: 'Foundation', description: 'Core JSON and Text tools launched with offline-first capabilities.', date: 'Q1 2026', icon: <BuildIcon /> },
  { stage: 'Phase 2', title: 'Spatial expansion', description: 'Advanced GeoJSON and Telemetry tools with rich map visualizations.', date: 'Q2 2026', icon: <PublicIcon /> },
  { stage: 'Phase 3', title: 'AI Integration', description: 'Smart code generation, parsing assistants, and AI-assisted debugging.', date: 'Q3 2026', icon: <AutoAwesomeIcon /> },
  { stage: 'Future', title: 'Collaborative Workspace', description: 'Real-time tool sharing, environments, and team dashboards.', date: '2027+', icon: <RocketLaunchIcon /> },
];

function About() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ bgcolor: 'background.default', height: '100%', overflowY: 'auto', pb: 10 }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        py: { xs: 10, md: 16 },
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        {/* Subtle background noise/gradient */}
        <Box sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 70%)'
            : `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
          zIndex: 0
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 900, letterSpacing: 4, mb: 2, display: 'block' }}>
              OUR MISSION
            </Typography>
            <Typography variant="h1" sx={{
              fontWeight: 900,
              color: 'text.primary',
              mb: 4,
              fontSize: { xs: '2.5rem', md: '4.5rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
              Building the Swiss Army Knife for Modern Devs.
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 600, mx: 'auto', lineHeight: 1.8 }}>
              UmbrellaCo was built with a single goal: to consolidate the scattered developer tools we use every day into one high-performance, beautiful interface.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* The Story Section - Editorial Layout */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={5}>
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <Typography variant="h3" fontWeight={800} sx={{ mb: 3 }}>
                We were tired of <br /> 100 open tabs.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                Every time we needed to format JSON, decode a JWT, or compress an image, we were forced to search the web, upload sensitive data to random servers, and navigate ad-ridden interfaces.
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={7}>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }}>
              <Box sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>The UmbrellaCo Philosophy</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>✓</Box>
                    <Typography variant="body2" color="text.secondary"><strong>Zero Server Uploads:</strong> Your data never leaves your browser. 100% client-side processing for total privacy.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>✓</Box>
                    <Typography variant="body2" color="text.secondary"><strong>Lightning Fast:</strong> Built on modern web technologies to ensure tools load instantly and execute immediately.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>✓</Box>
                    <Typography variant="body2" color="text.secondary"><strong>Beautifully Designed:</strong> Because utility software shouldn't look like it was built in 1999.</Typography>
                  </Box>
                </Stack>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* The Builders / Team */}
      <Box sx={{ bgcolor: isDark ? alpha('#000', 0.2) : alpha(theme.palette.primary.main, 0.02), py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ mb: 8 }}>The Builder</Typography>
          <Grid container spacing={4} justifyContent="center">
            {/* Team Member 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div whileHover={{ y: -5 }}>
                <Paper elevation={0} sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: theme.shadows[isDark ? 0 : 4]
                }}>
                  <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 3, bgcolor: 'primary.main', fontSize: '2rem', fontWeight: 800 }}>SA</Avatar>
                  <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>SaK1620</Typography>
                  <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ mb: 2 }}>Lead Architect</Typography>
                  <Typography variant="body2" color="text.secondary">Obsessed with UI/UX, WebGL, and building things that run at 60fps.</Typography>
                </Paper>
              </motion.div>
            </Grid>
            {/* Team Member 2 */}
            {/* <Grid item xs={12} sm={6} md={4}>
              <motion.div whileHover={{ y: -5 }}>
                <Paper elevation={0} sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: theme.shadows[isDark ? 0 : 4]
                }}>
                  <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 3, bgcolor: 'secondary.main', fontSize: '2rem', fontWeight: 800 }}>SK</Avatar>
                  <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>Sarah Kim</Typography>
                  <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ mb: 2 }}>Systems Engineer</Typography>
                  <Typography variant="body2" color="text.secondary">Ensuring all parsing engines and spatial tools are robust and type-safe.</Typography>
                </Paper>
              </motion.div>
            </Grid> */}
          </Grid>
        </Container>
      </Box>

      {/* Vertical Timeline Roadmap */}
      <Container maxWidth="md" sx={{ py: 12 }}>
        <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ mb: 8 }}>Product Roadmap</Typography>
        <Box sx={{ position: 'relative', pl: { xs: 4, md: 0 } }}>
          {/* Timeline central line */}
          <Box sx={{
            position: 'absolute',
            top: 0, bottom: 0,
            left: { xs: 15, md: '50%' },
            width: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.2),
            transform: { md: 'translateX(-50%)' }
          }} />

          {roadmap.map((item, idx) => (
            <Box key={idx} sx={{
              display: 'flex',
              justifyContent: { md: idx % 2 === 0 ? 'flex-start' : 'flex-end' },
              mb: 6,
              position: 'relative'
            }}>
              {/* Timeline dot */}
              <Box sx={{
                position: 'absolute',
                top: 24,
                left: { xs: -33, md: '50%' },
                width: 16, height: 16,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                transform: { md: 'translateX(-50%)' },
                border: `4px solid ${theme.palette.background.default}`
              }} />

              <Paper elevation={0} sx={{
                width: { xs: '100%', md: '45%' },
                p: 4,
                borderRadius: 4,
                bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                position: 'relative',
              }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="overline" color="primary.main" fontWeight={800} letterSpacing={1}>{item.stage}</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>{item.title}</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{item.description}</Typography>
                <Typography variant="caption" color="text.disabled" fontWeight={700}>{item.date}</Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Social Proof Footer */}
      <Box sx={{ mt: 4, py: 8, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 56, height: 56, border: `2px solid ${theme.palette.background.paper}` } }}>
              <Avatar alt="Dev 1" src="https://i.pravatar.cc/150?u=1" />
              <Avatar alt="Dev 2" src="https://i.pravatar.cc/150?u=2" />
              <Avatar alt="Dev 3" src="https://i.pravatar.cc/150?u=3" />
              <Avatar alt="Dev 4" src="https://i.pravatar.cc/150?u=4" />
              <Avatar alt="Dev 5" src="https://i.pravatar.cc/150?u=5" />
            </AvatarGroup>
            <Box>
              <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>Join 100+ developers</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                UmbrellaCo is completely open-source. Help us build the ultimate toolkit.
              </Typography>
            </Box>
            {/* <Button 
              variant="contained" 
              color="primary" 
              size="large"
              startIcon={<GitHubIcon />}
              href="https://github.com/umbrellaco" 
              target="_blank"
              sx={{ mt: 2, borderRadius: 50, px: 4, py: 1.5, fontWeight: 800 }}
            >
              Star us on GitHub
            </Button> */}
          </Box>
        </Container>
      </Box>

    </Box>
  );
}

export default About;